import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  Stack,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { Link as RouterLink, useLocation } from "react-router-dom";
import DownloadAppModal from "./DownloadApp";

const navItems = [
  { label: "Home", path: "/" },
  { label: "Products", path: "/products" },
  // { label: "calculators", path: "/sip-calculator" },
  { label: "Calculators", path: "/main-calculator" },
  { label: "About Us", path: "/aboutUs" },
  { label: "Contact Us", path: "/ContactUs" },
  { label: "News", path: "/rss-feed" },
];

const MotionButton = motion(Button);

const Navbar = () => {
  const theme = useTheme();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  return (
    <>
      {/* NAVBAR */}
      <AppBar
        elevation={0}
        sx={{
          position: "fixed",
          backgroundColor: theme.palette.secondary.main,
          backdropFilter: "blur(14px)",
          borderBottom: `1px solid ${theme.palette.primary.main}40`,
        }}
      >
        {/* <Toolbar sx={{ maxWidth: 1300, mx: "auto", width: "100%" }}> */}
        <Toolbar
          sx={{
            maxWidth: 1300,
            mx: "auto",
            width: "100%",
            px: { xs: 2, sm: 3, md: 8 },
            // backgroundColor: { xs: "green", sm: "red", md: "yellow" }
          }}
        >
          {/* LOGO */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              flexGrow: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 600,
                color: theme.palette.text.primary,
                letterSpacing: 0.5,
                fontFamily: " sans-serif",
              }}
            >
              Arth
              <span style={{ color: theme.palette.primary.main }}>Gyan</span>
            </Typography>
          </Box>

          {/* DESKTOP MENU */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 3 }}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <MotionButton
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  whileHover={{ y: -2 }}
                  sx={{
                    color: isActive
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    fontWeight: 400,
                    textTransform: "none",
                    fontSize: 15,
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: isActive ? "100%" : "0%",
                      height: "2px",
                      bottom: -4,
                      left: 0,
                      backgroundColor: theme.palette.primary.main,
                      transition: "width 0.3s ease",
                    },
                    "&:hover": {
                      color: theme.palette.primary.main,
                    },
                    "&:hover::after": {
                      width: "100%",
                    },
                  }}
                >
                  {item.label}
                </MotionButton>
              );
            })}
          </Box>

          {/* CTA DESKTOP */}
          <Box sx={{ display: { xs: "none", md: "block" }, ml: 3 }}>
            <MotionButton
              whileHover={{ scale: 1.05 }}
              onClick={() => setShowDownloadModal(true)}
              sx={{
                px: 3,
                py: 1,
                borderRadius: "8px",
                fontWeight: 600,
                backgroundColor: "#0080a7",
                color: "#fff",
                textTransform: "none",
                boxShadow: `0 6px 18px ${theme.palette.primary.main}55`,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Download App
            </MotionButton>
          </Box>

          {/* MOBILE ICON */}
          <IconButton
            onClick={() => setOpen(true)}
            sx={{ display: { xs: "flex", md: "none" }, ml: 1 }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* MOBILE DRAWER */}
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            width: 280,
            p: 3,
            backgroundColor: theme.palette.secondary.main,
            height: "100%",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Arth
              <span style={{ color: theme.palette.primary.main }}>Gyan</span>
            </Typography>

            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <Button
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  onClick={() => setOpen(false)}
                  sx={{
                    justifyContent: "flex-start",
                    color: isActive
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    fontWeight: 500,
                    textTransform: "none",
                    borderRadius: "8px",
                    backgroundColor: isActive
                      ? theme.palette.tertiary.main
                      : "transparent",
                    "&:hover": {
                      backgroundColor: theme.palette.tertiary.main,
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}

            <Button
              onClick={() => setShowDownloadModal(true)}
              sx={{
                mt: 2,
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                fontWeight: 600,
                borderRadius: "8px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Download App
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* MODAL */}
      <DownloadAppModal
        open={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
      />
    </>
  );
};

export default Navbar;